import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface IPagination {
    requestAnotherPage: () => void;
    currentPage: number;
    isLastPage: boolean;
}

// eslint-disable-next-line react-compiler/react-compiler
export const usePagination = (
    pagination: IPagination | undefined,
    resultSetId: string | number | undefined
) => {
    "use no memo";

    const requestingRef = useRef<string>(); // ref as well as state so as to update immediately
    const [isRequestingAnotherPage, setIsRequestingAnotherPage] =
        useState(false);

    const requestAnotherPageRef = useRef<() => void>();
    const currentPageRef = useRef<number>();
    const isLastPageRef = useRef(false);
    const resultSetIdRef = useRef<string | number>();

    // updating ref objects immediately so that request another page wrapper always has latest values
    // NOTE: this isn't a valid pattern for react concurrent mode
    // eslint-disable-next-line react-compiler/react-compiler
    requestAnotherPageRef.current = pagination?.requestAnotherPage;
    // eslint-disable-next-line react-compiler/react-compiler
    currentPageRef.current = pagination?.currentPage;
    // eslint-disable-next-line react-compiler/react-compiler
    isLastPageRef.current = pagination?.isLastPage ?? false;
    // eslint-disable-next-line react-compiler/react-compiler
    resultSetIdRef.current = resultSetId;

    const requestAnotherPageWrapper = useCallback(() => {
        if (!requestAnotherPageRef.current) {
            return;
        }
        if (isLastPageRef.current) {
            return;
        }
        const id = `${resultSetIdRef.current?.toString() ?? ""}_${currentPageRef.current?.toString() ?? ""}`;
        if (requestingRef.current === id) {
            return;
        }

        requestingRef.current = id;
        setIsRequestingAnotherPage(true);
        requestAnotherPageRef.current();
    }, []);

    const id = `${resultSetId?.toString() ?? ""}_${pagination?.currentPage.toString() ?? ""}`;
    useEffect(() => {
        if (
            requestingRef.current !== undefined &&
            requestingRef.current !== id
        ) {
            requestingRef.current = undefined;
            setIsRequestingAnotherPage(false);
        }
    }, [id]);

    const isLastPage = pagination?.isLastPage;
    const convertedPagination = useMemo(
        () => ({
            requestAnotherPage: requestAnotherPageWrapper,
            isRequestingAnotherPage,
            isLastPage
        }),
        [isRequestingAnotherPage, isLastPage, requestAnotherPageWrapper]
    );

    return pagination ? convertedPagination : {};
};
